import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setJasperIframeOpen } from '../features/jasperIframe/jasperIframeSlice';
import JasperBookListComponent from '../components/books/JasperBookListComponent';
import { RootState } from '../store';
import { NoInternetModal } from '../components/common/NoInternetModal';
import NavList from '../components/common/NavList';
import LoadingSpinner from '../components/layout/LoadingSpinner';
import JasperHeader from './JasperHeader';
import { useBookLibraryFetching } from '../customHooks/useBookLibraryFetching';
import { setIsJasperLoading } from '../features/jasper/jasperSlice';
import { PlatformType } from '../types/platforms';

const Jasper = () => {
    const dispatch = useDispatch();
    const [activeClass, setActiveClass] = useState('');
    const [isIframeLoading, setIsIframeLoading] = useState(true);

    const {
        classSubjectsData,
        classOptions,
        fetchBooks,
        isClassSubjectsLoading,
        isBooksLoading,
        hasError,
    } = useBookLibraryFetching('jasper');
    const product = useSelector(
        (state: RootState) => state.product
    ) as PlatformType;
    const jasperIframeUrl = useSelector(
        (state: RootState) => state.jasperIframe.url
    );
    const isOfflineModalOpen = useSelector(
        (state: RootState) => state.offlineModal.isOpen
    );
    const isJasperIframeOpen = useSelector(
        (state: RootState) => state.jasperIframe.isOpen
    );

    useEffect(() => {
        if (!classOptions || classOptions.length === 0) return;
        if (!localStorage.getItem('jasper-class-drop-down')) {
            handleClassChange('all');
            return;
        }
        const last_selected_class = JSON.parse(
            localStorage.getItem('jasper-class-drop-down') || ''
        );
        handleClassChange(
            Array.isArray(last_selected_class)
                ? 'all'
                : last_selected_class.toString()
        );
    }, [classOptions]);

    const handleClassChange = (value: string) => {
        dispatch(setIsJasperLoading(true));
        if (value !== 'all') {
            setActiveClass(value);
            localStorage.setItem('jasper-class-drop-down', value);
            fetchBooks(value);
        } else {
            const filtered_val = classOptions
                .filter((obj) => obj.value != 'all')
                .map((obj) => obj.value);
            localStorage.setItem(
                'jasper-class-drop-down',
                JSON.stringify(filtered_val)
            );
            fetchBooks(filtered_val);
            setActiveClass('all');
        }
    };

    const handleLoad = () => {
        setIsIframeLoading(false);
    };

    useEffect(() => {
        // Cleanup: close the iframe if open when unmounting
        return () => {
            dispatch(setJasperIframeOpen(false));
        };
    }, [dispatch]);

    useEffect(() => {
        const handleBackButtonClick = (event: MessageEvent) => {
            if (event.data.type === 'CLOSE_IFRAME') {
                dispatch(setJasperIframeOpen(false));
            }
        };

        window.addEventListener('message', handleBackButtonClick);
        return () => {
            window.removeEventListener('message', handleBackButtonClick);
        };
    });

    // Transform classOptions to match NavList expected format
    const navListItems = classOptions.map((option: any) => ({
        value: option.value,
        label: option.label,
    }));

    // Show loading state while fetching initial data
    if (isClassSubjectsLoading) {
        return (
            <div className="w-full h-full flex items-center justify-center">
                <LoadingSpinner />
            </div>
        );
    }

    // Show error state if there's an error fetching data
    if (hasError) {
        return (
            <div className="w-full h-full flex items-center justify-center">
                <div className="text-center">
                    <p className="text-red-500">Error loading class data</p>
                    <p className="text-gray-600">Please try again later</p>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full h-full ">
            {!isJasperIframeOpen && (
                <div className="flex flex-col gap-2 items-start text-black ">
                    {/* Search Component */}
                    <div className="flex-col gap-0 w-full">
                        <JasperHeader />
                        {/* NavList with dynamic class options  */}
                        {product!== 'Buildr' && (<div className="flex items-center w-full">
                            <NavList
                                items={navListItems}
                                activeValue={activeClass}
                                onChange={handleClassChange}
                                variant="underline"
                                size="md"
                                className="bg-white shadow-sm w-full"
                            />
                        </div>)}
                         
                    </div>
                </div>
            )}
            <>
                {isOfflineModalOpen ? <NoInternetModal /> : <></>}
                {isJasperIframeOpen ? (
                    <>
                        <div
                            className="w-auto
                             h-full"
                        >
                            {isIframeLoading && (
                                <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
                                    <LoadingSpinner />
                                </div>
                            )}
                            <div className="flex flex-row-reverse w-full h-full custom-scrollbar">
                                <iframe
                                    className="content-iframe w-full h-full transition-opacity duration-500 ease-in-out custom-scrollbar"
                                    src={
                                        jasperIframeUrl +
                                        `&timeStamp=${new Date().getTime()}`
                                    }
                                    onLoad={handleLoad}
                                    style={{
                                        opacity: isIframeLoading ? 0 : 1,
                                    }}
                                ></iframe>
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="h-screen w-full overflow-y-scroll pb-32 custom-scrollbar fixed ">
                        {/* Show loading spinner while books are being fetched */}
                        {isBooksLoading ? (
                            <div className="flex items-center justify-center h-full w-full">
                                <LoadingSpinner />
                            </div>
                        ) : (
                            <div className="flex-col w-full h-full">
                                <div className="flex flex-col ml-12 mb-4 mt-4">
                                    <h3 className="text-xl text-black ">
                                        Create Assessment{' '}
                                        {product != 'Buildr' && (
                                            <span>or Worksheet</span>
                                        )}
                                    </h3>
                                    <p className=" opacity-50 text-[#666666] ">
                                        Select the book you want to create
                                        assessment{' '}
                                        {product != 'Buildr' && (
                                            <span>or worksheet</span>
                                        )}{' '}
                                        for.
                                    </p>
                                </div>
                                <JasperBookListComponent classSubjects={classSubjectsData?.data?.classSubjects}/>
                            </div>
                        )}
                    </div>
                )}
            </>
        </div>
    );
};

export default Jasper;
